<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { InjectionSession, type SessionDataSet } from '~/components/reports/injectionSession';
import UpgradeIntermediate from '~/pages/upgrade-intermediate.vue';

interface HoleGraphData {
  holeNo: number
  startTime: string
  eventTypeId: number | null
  eventType: string | null
  data?: Array<{ timestamp: string, flow: number, pressure: number, volume: number, cement: number }>
}

const props = defineProps({
  holeGraphData: {
    type: Array as () => HoleGraphData[],
    required: true,
    default: () => []
  }
})
const { hasIntermediateAccess } = useAuth();
const selectedHoleNo = ref<number | null>(null)
const selectedHoleData = computed(() => {
  return props.holeGraphData.find(h => h.holeNo === selectedHoleNo.value) ?? null
})

const selectHole = (holeNo: number) => {
  selectedHoleNo.value = holeNo
}

const formatTime = (date: string | number | Date) => {
  if (!date) return ''
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

const getStatusClass = (eventTypeId: number | null) => {
  if (eventTypeId === 11) return 'status-pressure'
  if (eventTypeId === 13) return 'status-amount'
  if (eventTypeId === 14) return 'status-cement'
  return ''
}


const holeStats = computed(() => {
  const d = selectedHoleData.value?.data
  if (!d?.length) return null
  const endTime = d[d.length - 1].timestamp
  const durationMs = new Date(endTime).getTime() - new Date(selectedHoleData.value!.startTime).getTime()
  const durationMin = Math.round(durationMs / 60000)
  const peakFlow = Math.max(...d.map(p => p.flow))
  const peakPressure = Math.max(...d.map(p => p.pressure))
  const totalVolume = d[d.length - 1].volume
  const totalCement = d[d.length - 1].cement
  return { endTime, durationMin, peakFlow, peakPressure, totalVolume, totalCement }
})

const chartContainer = ref<HTMLDivElement | null>(null)
let chartSession: InjectionSession | null = null

const initChart = () => {
  if (!chartContainer.value || !selectedHoleData.value) return

  chartSession = new InjectionSession({
    chartTitle: `Hole ${selectedHoleData.value.holeNo}`,
    container: chartContainer.value
  })

  if (selectedHoleData.value.data?.length) {
    chartSession.add(selectedHoleData.value.data as unknown as SessionDataSet[])
  }
}

watch(selectedHoleData, async (newVal) => {
  if (chartSession) {
    chartSession.injectionChart.chart.dispose()
    chartSession = null
  }
  if (newVal) {
    await nextTick()
    initChart()
  }
})

onBeforeUnmount(() => {
  if (chartSession) {
    chartSession.injectionChart.chart.dispose()
    chartSession = null
  }
})
</script>

<template>
  <UpgradeIntermediate v-if="!hasIntermediateAccess" style="min-height: 400px;" />
  <v-card v-else class="d-flex flex-row overflow-hidden" style="height: calc(100vh - 210px);" elevation="2">
    <!-- Sidebar -->
    <div class="d-flex flex-column border-e bg-grey-lighten-5" style="width: 280px; min-width: 280px;">
      <div class="pa-4 border-b d-flex justify-space-between align-center bg-white">
        <span class="text-subtitle-2 font-weight-bold text-uppercase text-medium-emphasis">Select Hole</span>
        <v-chip size="x-small" variant="flat" color="grey-lighten-3" class="font-weight-bold text-medium-emphasis">
          {{ holeGraphData.length }}
        </v-chip>
      </div>

      <v-list class="pa-0 overflow-y-auto flex-grow-1 bg-transparent" density="compact">
        <v-list-item
          v-for="hole in holeGraphData"
          :key="hole.holeNo"
          :value="hole.holeNo"
          :active="selectedHoleNo === hole.holeNo"
          @click="selectHole(hole.holeNo)"
          class="hole-item border-b rounded-0"
          :class="getStatusClass(hole.eventTypeId)"
          active-class="active-hole"
          color="primary"
        >
          <div class="d-flex justify-space-between align-center w-100 py-2">
            <div>
              <div class="font-weight-bold text-body-2 text-high-emphasis">Hole {{ hole.holeNo }}</div>
              <div class="text-caption text-medium-emphasis">{{ formatTime(hole.startTime) }}</div>
              <div v-if="hole.eventType" class="text-caption font-weight-bold text-grey-darken-1 mt-1">
                {{ hole.eventType }}
              </div>
            </div>
            <v-icon icon="mdi-chevron-right" size="small" color="grey-lighten-1"></v-icon>
          </div>
        </v-list-item>
      </v-list>
    </div>

    <!-- Main Content -->
    <div class="flex-grow-1 d-flex flex-column bg-white" style="min-width: 0;">
      <template v-if="selectedHoleData">
        <!-- Compact header -->
        <div class="px-4 py-3 border-b d-flex flex-wrap align-center bg-white" style="gap: 12px;">
          <div class="d-flex align-center gap-2 mr-2">
            <span class="text-subtitle-1 font-weight-bold text-blue-grey-darken-3">Hole {{ selectedHoleData.holeNo }}</span>
            <v-chip v-if="selectedHoleData.eventTypeId" size="x-small" variant="tonal" color="primary" class="font-weight-bold text-uppercase">
              {{ selectedHoleData.eventType }}
            </v-chip>
          </div>
          <v-divider vertical class="mx-1" style="height:24px;align-self:center;" />
          <div class="d-flex flex-wrap text-caption text-medium-emphasis align-center" style="gap: 12px;">
            <span><strong>Start:</strong> {{ formatTime(selectedHoleData.startTime) }}</span>
            <span v-if="holeStats"><strong>End:</strong> {{ formatTime(holeStats.endTime) }}</span>
            <span v-if="holeStats"><strong>Duration:</strong> {{ holeStats.durationMin }} min</span>
            <v-divider vertical class="mx-1" style="height:20px;align-self:center;" />
            <span v-if="holeStats"><strong>Peak Flow:</strong> {{ holeStats.peakFlow.toFixed(1) }} L/min</span>
            <span v-if="holeStats"><strong>Peak Pressure:</strong> {{ holeStats.peakPressure.toFixed(1) }} bar</span>
            <span v-if="holeStats"><strong>Total Volume:</strong> {{ holeStats.totalVolume.toFixed(0) }} L</span>
            <span v-if="holeStats"><strong>Total Cement:</strong> {{ holeStats.totalCement.toFixed(0) }} kg</span>
          </div>
        </div>

        <!-- Chart -->
        <div ref="chartContainer" class="flex-grow-1 overflow-hidden"></div>
      </template>

      <div v-else class="d-flex align-center justify-center h-100 bg-grey-lighten-5">
        <div class="text-center">
          <v-icon icon="mdi-gesture-tap" size="64" color="grey-lighten-1" class="mb-4"></v-icon>
          <h3 class="text-h6 font-weight-medium text-grey-darken-1">Select a hole</h3>
          <p class="text-body-2 text-medium-emphasis">Choose a hole from the list to view detailed graph analysis.</p>
        </div>
      </div>
    </div>
  </v-card>
</template>

<style scoped>
.hole-item {
  border-left: 4px solid transparent;
  transition: all 0.2s;
}

.hole-item:hover {
  background-color: #f1f5f9;
}

.active-hole {
  background-color: #fff !important;
  border-left-color: #002d5a !important;
}

.hole-item.status-pressure { border-left-color: #f59e0b !important; }
.hole-item.status-amount   { border-left-color: #3b82f6 !important; }
.hole-item.status-cement   { border-left-color: #10b981 !important; }
</style>